import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import PageHeader from "@/components/PageHeader";
import QolProbabilitiesChart from "@/components/QolProbabilitiesChart";
import RequestCoachingButton from "@/components/RequestCoachingButton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Smile, Meh, Frown, Heart, Clock, Compass, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const SUBJECTS = [
  { key: "feel", labelKey: "qol.subject.feel", icon: Heart },
  { key: "past", labelKey: "qol.subject.past", icon: Clock },
  { key: "future", labelKey: "qol.subject.future", icon: Compass },
  { key: "inner_self_talk", labelKey: "qol.subject.inner_self_talk", icon: MessageCircle },
];

const OPTIONS = [
  { value: "good", labelKey: "qol.option.good", icon: Smile, color: "#4A5D4E" },
  { value: "neutral", labelKey: "qol.option.neutral", icon: Meh, color: "#B37A5F" },
  { value: "bad", labelKey: "qol.option.bad", icon: Frown, color: "#9C6A53" },
];

export default function QualityOfLife() {
  const { user } = useAuth();
  const { t } = useT();
  const [state, setState] = useState({ feel: null, past: null, future: null, inner_self_talk: null });
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/qol/me");
        setState({
          feel: data.feel ?? null,
          past: data.past ?? null,
          future: data.future ?? null,
          inner_self_talk: data.inner_self_talk ?? null,
        });
        setUpdatedAt(data.updated_at);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const setValue = async (key, value) => {
    if (!value) return; // toggle-group emits "" when deselecting; ignore
    const prev = state[key];
    setState((s) => ({ ...s, [key]: value }));
    try {
      const { data } = await api.put("/qol/me", { [key]: value });
      setUpdatedAt(data.updated_at);
    } catch (e) {
      setState((s) => ({ ...s, [key]: prev }));
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="px-8 lg:px-12 py-12 max-w-5xl" data-testid="quality-of-life-page">
      <PageHeader
        overline={user?.name?.split(" ")[0] || ""}
        title={t("qol.title")}
        description={t("qol.description")}
      />

      {loading ? (
        <p className="text-sm text-[#8A9188] uppercase tracking-[0.2em] font-bold">{t("common.loading")}</p>
      ) : (
        <>
          <QolProbabilitiesChart />

          <div className="space-y-5 mt-12" data-testid="qol-subjects">
            {SUBJECTS.map(({ key, labelKey, icon: Icon }) => {
              const current = state[key];
              return (
                <div
                  key={key}
                  className="bg-white border border-[#E5E2DC] rounded-2xl p-6 sm:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6"
                  data-testid={`qol-subject-${key}`}
                >
                  <div className="flex items-start gap-4 max-w-xl">
                    <div className="h-11 w-11 rounded-xl bg-[#F2F0EA] flex items-center justify-center shrink-0">
                      <Icon strokeWidth={1.5} className="text-[#B37A5F]" size={20} />
                    </div>
                    <div>
                      <h3 className="font-heading text-xl sm:text-2xl text-[#1A1D1A] tracking-tight font-semibold">
                        {t(labelKey)}
                      </h3>
                    </div>
                  </div>

                  <ToggleGroup
                    type="single"
                    value={current || ""}
                    onValueChange={(v) => setValue(key, v)}
                    className="gap-2 self-start md:self-center"
                    data-testid={`qol-toggle-${key}`}
                  >
                    {OPTIONS.map(({ value, labelKey: optLabelKey, icon: OptIcon, color }) => {
                      const active = current === value;
                      const optLabel = t(optLabelKey);
                      return (
                        <ToggleGroupItem
                          key={value}
                          value={value}
                          aria-label={optLabel}
                          data-testid={`qol-${key}-${value}`}
                          className={`h-11 px-4 rounded-xl border transition-colors ${
                            active
                              ? "text-white"
                              : "bg-white border-[#E5E2DC] text-[#5C635A] hover:bg-[#F2F0EA] hover:text-[#1A1D1A]"
                          }`}
                          style={
                            active
                              ? { backgroundColor: color, borderColor: color, color: "#fff" }
                              : undefined
                          }
                        >
                          <OptIcon strokeWidth={1.5} size={16} className="mr-1.5" />
                          {optLabel}
                        </ToggleGroupItem>
                      );
                    })}
                  </ToggleGroup>
                </div>
              );
            })}
          </div>

          <RequestCoachingButton clientType={user?.client_type} />
        </>
      )}
    </div>
  );
}
